// src/app/tools/seo-meta-generator/page.tsx
'use client';

import { useState } from 'react';
import { Copy, RefreshCw, Search, AlertCircle, Check } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { seoMetaGeneratorContent } from '../../data/seo-meta-generator-content';

export default function SEOMetaGenerator() {
    const [url, setUrl] = useState('');
    const [keyword, setKeyword] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [location, setLocation] = useState('');
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState({ title: false, description: false, all: false });

    const generateMeta = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!keyword.trim()) {
            setError('Please enter a target keyword');
            return;
        }

        setIsGenerating(true);
        setError('');

        try {
            const response = await fetch('/api/generate/seo-meta', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    keyword,
                    businessName: businessName || undefined,
                    location: location || undefined,
                    url: url || undefined,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to generate meta tags');
            }

            const data = await response.json();
            setMetaTitle(data.metaTitle);
            setMetaDescription(data.metaDescription);
        } catch (err) {
            console.error('Error generating meta tags:', err);
            setError('Failed to generate meta tags. Please try again.');
            // Fallback to mock data
            setMetaTitle(generateMockMetaTitle(keyword, businessName, location));
            setMetaDescription(generateMockMetaDescription(keyword, businessName, location));
        } finally {
            setIsGenerating(false);
        }
    };

    const copyToClipboard = (text: string, type: 'title' | 'description' | 'all') => {
        navigator.clipboard.writeText(text);
        setCopied({ ...copied, [type]: true });
        setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
    };

    const resetForm = () => {
        setUrl('');
        setKeyword('');
        setBusinessName('');
        setLocation('');
        setMetaTitle('');
        setMetaDescription('');
        setError('');
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <ToolPageIntro title="SEO Meta Generator - Free Online Meta Tags Creator">
                Generate optimized SEO meta tags, titles, and descriptions with our free online tool. Improve your website search rankings and click-through rates.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <form onSubmit={generateMeta} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="keyword" className="block text-sm font-medium text-gray-700 mb-1">
                                Target Keyword * <span className="text-xs text-gray-500">(required)</span>
                            </label>
                            <input
                                type="text"
                                id="keyword"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                placeholder="e.g., best pizza, web design services"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
                                Page URL <span className="text-xs text-gray-500">(optional)</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="url"
                                    id="url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://example.com/page"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                                Business/Website Name <span className="text-xs text-gray-500">(optional)</span>
                            </label>
                            <input
                                type="text"
                                id="businessName"
                                value={businessName}
                                onChange={(e) => setBusinessName(e.target.value)}
                                placeholder="e.g., Joe's Pizza, Acme Web Design"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                                Location <span className="text-xs text-gray-500">(optional)</span>
                            </label>
                            <input
                                type="text"
                                id="location"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                placeholder="e.g., New York, London, Online"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-red-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Reset
                        </button>
                        <button
                            type="submit"
                            disabled={isGenerating}
                            className={`px-6 py-2 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isGenerating
                                ? 'bg-blue-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                        >
                            {isGenerating ? (
                                <>
                                    <RefreshCw className="animate-spin h-5 w-5 mr-2 inline" />
                                    Generating...
                                </>
                            ) : (
                                'Generate Meta Tags'
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {(metaTitle || metaDescription) && (
                <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Generated Meta Tags</h2>
                        <button
                            onClick={() => copyToClipboard(`${metaTitle}\n\n${metaDescription}`, 'all')}
                            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center text-sm font-medium"
                        >
                            {copied.all ? (
                                <>
                                    <Check className="h-4 w-4 mr-2" />
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copy All
                                </>
                            )}
                        </button>
                    </div>

                    <div className="space-y-6">
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="font-medium text-gray-900">Meta Title</h3>
                                <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">
                                        {metaTitle.length}/60 characters
                                    </span>
                                    <button
                                        onClick={() => copyToClipboard(metaTitle, 'title')}
                                        className="text-gray-500 hover:text-blue-600 p-1 rounded-full hover:bg-gray-100"
                                        title="Copy to clipboard"
                                    >
                                        {copied.title ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="p-4">
                                <p className="text-gray-800">{metaTitle}</p>
                                <div
                                    className={`mt-1 h-1 rounded-full ${metaTitle.length > 60 ? 'bg-red-500' : 'bg-green-500'
                                        }`}
                                    style={{ width: `${Math.min(100, (metaTitle.length / 60) * 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="font-medium text-gray-900">Meta Description</h3>
                                <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">
                                        {metaDescription.length}/160 characters
                                    </span>
                                    <button
                                        onClick={() => copyToClipboard(metaDescription, 'description')}
                                        className="text-gray-500 hover:text-blue-600 p-1 rounded-full hover:bg-gray-100"
                                        title="Copy to clipboard"
                                    >
                                        {copied.description ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="p-4">
                                <p className="text-gray-800">{metaDescription}</p>
                                <div
                                    className={`mt-1 h-1 rounded-full ${metaDescription.length > 160 ? 'bg-red-500' : 'bg-green-500'
                                        }`}
                                    style={{ width: `${Math.min(100, (metaDescription.length / 160) * 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg
                                        className="h-5 w-5 text-blue-400"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">SEO Tips</h3>
                                    <div className="mt-2 text-sm text-blue-700 space-y-1">
                                        <p>• Keep meta titles under 60 characters</p>
                                        <p>• Include your target keyword near the beginning</p>
                                        <p>• Write compelling descriptions between 120-160 characters</p>
                                        <p>• Use action-oriented language in your description</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={seoMetaGeneratorContent.title}
                    description={seoMetaGeneratorContent.description}
                    content={seoMetaGeneratorContent.content}
                    faqs={seoMetaGeneratorContent.faqs}
                    relatedTools={seoMetaGeneratorContent.relatedTools}
                />
            </div>
        </div>
    );
}

// Mock data generators for development
function generateMockMetaTitle(keyword: string, businessName?: string, location?: string): string {
    const titleParts = [];

    // Add keyword variations
    const keywordVariations = [
        `Best ${keyword}`,
        `Top ${keyword}`,
        `${keyword} Services`,
        `${keyword} Guide`,
        `Find ${keyword}`
    ];

    titleParts.push(keywordVariations[Math.floor(Math.random() * keywordVariations.length)]);

    // Add business name if provided
    if (businessName) {
        titleParts.push(`| ${businessName}`);
    }

    // Add location if provided
    if (location) {
        titleParts.push(`in ${location}`);
    }

    // Add current year for freshness
    titleParts.push(`| ${new Date().getFullYear()}`);

    return titleParts.join(' ').substring(0, 60);
}

function generateMockMetaDescription(keyword: string, businessName?: string, location?: string): string {
    const descriptions = [
        `Discover the best ${keyword} ${businessName ? `from ${businessName}` : ''} ${location ? `in ${location}` : ''}. ` +
        `Get expert advice, compare options, and find the perfect solution for your needs.`,

        `Looking for ${keyword}? ${businessName || 'We'} offers top-rated ${keyword} services ` +
        `${location ? `in ${location}` : 'worldwide'}. Contact us today for a free consultation!`,

        `Your complete guide to ${keyword}. Learn about the best options, prices, and tips for finding the perfect ` +
        `${keyword} ${businessName ? `from ${businessName}` : ''} ${location ? `in ${location}` : ''}.`,

        `Find the best ${keyword} near you. ${businessName || 'We'} provides high-quality ${keyword} services ` +
        `${location ? `in ${location}` : 'worldwide'}. Get started today!`
    ];

    return descriptions[Math.floor(Math.random() * descriptions.length)].substring(0, 160);
}